import React, { useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import api from '../services/api';
import { useSnackbar } from '../context/SnackbarContext';
import './StripePayment.css';

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

interface StripePaymentProps {
  amount: number;
  onSuccess: () => void;
  onCancel: () => void;
}

const PaymentForm: React.FC<StripePaymentProps> = ({ amount, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [billingPostalCode, setBillingPostalCode] = useState('');
  const [feeAmount, setFeeAmount] = useState<number | null>(null);
  const [totalCharge, setTotalCharge] = useState<number | null>(null);
  const { showSuccess, showError } = useSnackbar();

  const trimmedName = cardholderName.trim();
  const postalCodeValue = billingPostalCode.trim().toUpperCase();
  const isCanadianPostal = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(postalCodeValue);
  const isUsZip = /^\d{5}(-\d{4})?$/.test(postalCodeValue);
  const postalCodeValid = isCanadianPostal || isUsZip;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    try {
      if (!trimmedName) {
        showError('Enter the cardholder name.');
        setProcessing(false);
        return;
      }

      if (!postalCodeValid) {
        showError('Enter a valid Canadian postal code (A1A 1A1) or US ZIP (12345).');
        setProcessing(false);
        return;
      }

      // Create payment intent
      const { data } = await api.post('/payments/create-payment-intent', {
        amount,
      });

      const { clientSecret, paymentIntentId, fee, totalCharge: totalFromServer } = data;
      if (typeof fee === 'number') {
        setFeeAmount(fee);
      }
      if (typeof totalFromServer === 'number') {
        setTotalCharge(totalFromServer);
      }

      // Confirm payment with Stripe
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement)!,
            billing_details: {
              name: trimmedName,
              address: {
                postal_code: postalCodeValue,
                country: isCanadianPostal ? 'CA' : isUsZip ? 'US' : undefined,
              },
            },
          },
        }
      );

      if (confirmError) {
        showError(confirmError.message || 'Payment failed');
        setProcessing(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        // Confirm payment on backend
        await api.post('/payments/confirm-payment', {
          paymentIntentId,
        });

        showSuccess(`Successfully topped up $${amount.toFixed(2)}`);
        onSuccess();
      }
    } catch (error: any) {
      showError(error.response?.data?.error || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#1a1a1a',
        '::placeholder': {
          color: '#999',
        },
      },
      invalid: {
        color: '#dc2626',
      },
    },
    hidePostalCode: true,
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-payment-form">
      <div className="payment-amount-display">
        <span>Amount to Pay:</span>
        <span className="amount-value">${amount.toFixed(2)}</span>
      </div>
      <div className="payment-amount-display">
        <span>Card processing fee:</span>
        <span className="amount-value">
          {feeAmount !== null ? `$${feeAmount.toFixed(2)}` : 'Calculated at checkout'}
        </span>
      </div>
      <div className="payment-amount-display">
        <span>Total charge:</span>
        <span className="amount-value">
          {totalCharge !== null ? `$${totalCharge.toFixed(2)}` : 'Calculated at checkout'}
        </span>
      </div>
      
      <div className="card-element-container">
        <label>Card Details</label>
        <div className="stripe-card-element">
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      <div className="form-group">
        <label>Cardholder Name</label>
        <input
          type="text"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          placeholder="Name on card"
          required
        />
      </div>

      <div className="form-group">
        <label>Billing Postal / ZIP Code</label>
        <input
          type="text"
          value={billingPostalCode}
          onChange={(e) => setBillingPostalCode(e.target.value.toUpperCase())}
          placeholder="A1A 1A1 or 12345"
          pattern="^[A-Za-z]\\d[A-Za-z][ -]?\\d[A-Za-z]\\d$|^(\\d{5}(-\\d{4})?)$"
          title="Canadian Postal Code (e.g., A1A 1A1) or US ZIP Code (e.g., 12345 or 12345-6789)"
          required
        />
        {!postalCodeValid && billingPostalCode && (
          <div className="error">Enter a valid Canadian postal code or US ZIP code.</div>
        )}
      </div>

      <div className="payment-actions">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!stripe || processing}
        >
          {processing ? 'Processing...' : `Pay $${amount.toFixed(2)}`}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={processing}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const StripePayment: React.FC<StripePaymentProps> = (props) => {
  const { showError } = useSnackbar();
  const stripeAvailable = useMemo(() => Boolean(stripePublishableKey), []);

  useEffect(() => {
    if (!stripeAvailable) {
      showError('Stripe is not configured. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY.');
    }
  }, [stripeAvailable, showError]);

  if (!stripeAvailable) {
    return (
      <div className="stripe-payment-form">
        <p className="payment-error">
          Stripe is not configured. Add your publishable key to the client `.env`.
        </p>
        <div className="payment-actions">
          <button type="button" className="btn btn-secondary" onClick={props.onCancel}>
            Close
          </button>
        </div>
      </div>
    );
  }
  const options = {
    appearance: {
      theme: 'stripe',
    },
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentForm {...props} />
    </Elements>
  );
};

export default StripePayment;
